import { create } from 'zustand';
import {
  Room,
  RoomEvent,
  Track,
  VideoQuality,
  type AudioCaptureOptions,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  type ScreenShareCaptureOptions,
  type TrackPublishDefaults,
  type TrackPublishOptions,
} from 'livekit-client';
import { getChannel, joinUserChannel } from '../lib/realtime';
import { useAuthStore } from './authStore';
import { useServerStore } from './serverStore';
import { useFriendStore } from './friendStore';
import { getDMCallToken, getVoiceChannelParticipants, getVoiceChannelToken } from '../services/livekitService';
import {
  playDialtone,
  playJoinOther,
  playLeave,
  playRingtone,
  stopDialtone,
  stopRingtone,
} from '../lib/sounds';
import {
  getScreenShareEncoding,
  getScreenShareResolution,
  getScreenShareSettings,
} from '../lib/screenShareSettings';

interface VoiceUser {
  id: string;
  username: string;
  avatar_url: string | null;
  muted: boolean;
}

type DmCallStatus = 'idle' | 'ringing' | 'dialing' | 'connected';

interface VoiceState {
  // Voice channel state
  isConnected: boolean;
  isMuted: boolean;
  connectedUsers: VoiceUser[];
  currentChannelId: string | null;
  currentChannelName: string | null;
  localStream: MediaStream | null;
  peerConnections: Record<string, RTCPeerConnection>;
  remoteStreams: Record<string, MediaStream>;
  userSpeakingStates: Record<string, number>;
  voiceChannelParticipants: Record<string, VoiceUser[]>;

  // DM call state
  dmCallStatus: DmCallStatus;
  dmCallId: string | null;
  dmCallUsers: VoiceUser[];
  dmCallPeers: Record<string, RTCPeerConnection>;
  dmCallRemoteStreams: Record<string, MediaStream>;
  dmCallSpeakingStates: Record<string, number>;

  // Video / screen share
  isCameraOn: boolean;
  isScreenSharing: boolean;
  channelVideoTracks: Record<string, { stream: MediaStream; source: 'camera' | 'screen' }>;
  dmCallVideoTracks: Record<string, { stream: MediaStream; source: 'camera' | 'screen' }>;

  callDuration: number;
  callTimerId: ReturnType<typeof setInterval> | null;

  // Incoming call
  isCallIncoming: boolean;
  incomingCallFrom: VoiceUser | null;
  incomingCallId: string | null;

  // Expanded panel
  isCallPanelExpanded: boolean;
  setCallPanelExpanded: (v: boolean) => void;

  // Actions
  joinVoiceChannel: (channelId: string) => Promise<MediaStream | null>;
  leaveVoiceChannel: () => void;
  fetchVoiceChannelParticipants: (channelId: string) => Promise<void>;
  fetchVisibleVoiceChannelParticipants: (channelIds: string[]) => Promise<void>;
  toggleMute: () => void;

  // DM call actions
  startDmCall: (recipientId: string) => Promise<void>;
  joinDmCall: (callId: string) => Promise<void>;
  answerDmCall: () => Promise<void>;
  leaveDmCall: () => void;
  rejectIncomingCall: () => void;
  toggleCamera: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;

  // Internal methods (kept for compatibility)
  _handleUserJoined: (user: VoiceUser) => void;
  _handleUserLeft: (userId: string) => void;
  _handleUserMuteChanged: (userId: string, muted: boolean) => void;
  _cleanupPeerConnections: () => void;
  _createChannelPeer: (_targetUserId: string, _isInitiator: boolean) => RTCPeerConnection | null;
  _createDmPeer: (_targetUserId: string, _isInitiator: boolean) => RTCPeerConnection | null;
  _startCallTimer: () => void;
  _stopCallTimer: () => void;
}

export type { VoiceUser, DmCallStatus };

const MIC_CONSTRAINTS: AudioCaptureOptions = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

const buildScreenShareCaptureOptions = (): ScreenShareCaptureOptions => {
  const settings = getScreenShareSettings();
  const resolution = getScreenShareResolution(settings);
  return {
    video: true,
    resolution: {
      width: resolution.width,
      height: resolution.height,
      frameRate: settings.fps,
    },
    contentHint: 'detail',
    systemAudio: 'include',
    selfBrowserSurface: 'exclude',
  };
};

const buildScreenSharePublishOptions = (): TrackPublishOptions => ({
  source: Track.Source.ScreenShare,
  screenShareEncoding: getScreenShareEncoding(),
  simulcast: false,
  degradationPreference: 'maintain-resolution',
});

const getRoomPublishDefaults = (): TrackPublishDefaults => ({
  screenShareEncoding: getScreenShareEncoding(),
});

const createLiveKitRoom = () => new Room({
  adaptiveStream: false,
  dynacast: false,
  publishDefaults: getRoomPublishDefaults(),
});

let channelRoom: Room | null = null;
let dmRoom: Room | null = null;
let dmPartnerUserId: string | null = null;
let attachedUserId: string | null = null;
let listenersAttached = false;
let channelMeterTimer: ReturnType<typeof setInterval> | null = null;
let dmMeterTimer: ReturnType<typeof setInterval> | null = null;
let screenShareDiagnosticsTimer: ReturnType<typeof setInterval> | null = null;
let incomingCallTimeout: ReturnType<typeof setTimeout> | null = null;
let isCleaningChannel = false;
let isCleaningDm = false;

const channelAudioElements: Record<string, HTMLAudioElement> = {};
const dmAudioElements: Record<string, HTMLAudioElement> = {};

function ensureUserChannel(userId: string) {
  joinUserChannel(userId);
  return getChannel(`user:${userId}`);
}

function stopAndClearIncomingTimeout() {
  if (incomingCallTimeout) {
    clearTimeout(incomingCallTimeout);
    incomingCallTimeout = null;
  }
}

function resetAudioElements(map: Record<string, HTMLAudioElement>) {
  for (const [id, audio] of Object.entries(map)) {
    audio.pause();
    audio.srcObject = null;
    delete map[id];
  }
}

function removeAudioElement(map: Record<string, HTMLAudioElement>, userId: string) {
  const audio = map[userId];
  if (!audio) return;
  audio.pause();
  audio.srcObject = null;
  delete map[userId];
}

function attachRemoteAudio(
  map: Record<string, HTMLAudioElement>,
  userId: string,
  track: { mediaStreamTrack?: MediaStreamTrack }
) {
  const mediaTrack = track.mediaStreamTrack;
  if (!mediaTrack) return;

  removeAudioElement(map, userId);

  const audio = new Audio();
  audio.autoplay = true;
  audio.srcObject = new MediaStream([mediaTrack]);
  map[userId] = audio;
  audio.play().catch(() => {});
}

function participantToUser(participant: RemoteParticipant): VoiceUser {
  return {
    id: participant.identity,
    username: participant.name || participant.identity,
    avatar_url: participant.metadata || null,
    muted: !participant.isMicrophoneEnabled,
  };
}

function requestBestScreenShareQuality(publication: RemoteTrackPublication) {
  if (publication.kind !== Track.Kind.Video || publication.source !== Track.Source.ScreenShare) {
    return;
  }

  const settings = getScreenShareSettings();
  const resolution = getScreenShareResolution(settings);
  publication.setSubscribed(true);
  publication.setVideoQuality(VideoQuality.HIGH);
  publication.setVideoDimensions({ width: resolution.width, height: resolution.height });
  publication.setVideoFPS(settings.fps);
}

type ScreenShareStatsSample = {
  timestamp: number;
  bytes: number;
};

type ScreenShareStatsRow = {
  direction: 'outbound' | 'inbound';
  participant: string;
  requested: string;
  captured: string;
  bitrate: string;
  fps: number | string;
  frames: string;
  transport: string;
};

const screenShareStatsSamples = new Map<string, ScreenShareStatsSample>();

function formatScreenShareTarget() {
  const settings = getScreenShareSettings();
  const resolution = getScreenShareResolution(settings);
  const encoding = getScreenShareEncoding(settings);
  return `${resolution.label} ${settings.fps}fps / ${(encoding.maxBitrate / 1_000_000).toFixed(0)} Mbps`;
}

function formatTrackSettings(track?: MediaStreamTrack) {
  if (!track) return 'n/a';
  const settings = track.getSettings();
  const width = settings.width ? `${settings.width}` : '?';
  const height = settings.height ? `${settings.height}` : '?';
  const frameRate = settings.frameRate ? ` ${Math.round(settings.frameRate)}fps` : '';
  return `${width}x${height}${frameRate}`;
}

function getStatNumber(stat: unknown, key: string): number | undefined {
  const value = (stat as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'number' ? value : undefined;
}

function findVideoRtpStat(stats: RTCStatsReport, direction: 'outbound' | 'inbound') {
  const targetType = direction === 'outbound' ? 'outbound-rtp' : 'inbound-rtp';
  for (const stat of stats.values()) {
    const record = stat as Record<string, unknown>;
    if (record.type !== targetType) continue;
    if (record.kind === 'video' || record.mediaType === 'video') {
      return stat;
    }
  }
  return undefined;
}

function getSelectedCandidatePair(stats: RTCStatsReport) {
  for (const stat of stats.values()) {
    const record = stat as Record<string, unknown>;
    if (record.type === 'candidate-pair' && record.state === 'succeeded' && record.nominated) {
      return record;
    }
  }
  for (const stat of stats.values()) {
    const record = stat as Record<string, unknown>;
    if (record.type === 'transport' && typeof record.selectedCandidatePairId === 'string') {
      return stats.get(record.selectedCandidatePairId) as Record<string, unknown> | undefined;
    }
  }
  return undefined;
}

function describeCandidate(stats: RTCStatsReport, id: unknown) {
  if (typeof id !== 'string') return '';
  const candidate = stats.get(id) as Record<string, unknown> | undefined;
  if (!candidate) return '';
  const protocol = String(candidate.protocol || candidate.relayProtocol || '').toUpperCase();
  const type = String(candidate.candidateType || '');
  return [protocol, type].filter(Boolean).join('/');
}

function describeTransport(stats: RTCStatsReport) {
  const pair = getSelectedCandidatePair(stats);
  if (!pair) return 'unknown';
  const local = describeCandidate(stats, pair.localCandidateId);
  const remote = describeCandidate(stats, pair.remoteCandidateId);
  return local || remote ? `${local || '?'} -> ${remote || '?'}` : 'selected';
}

function calculateBitrateMbps(key: string, stats: RTCStatsReport, stat: unknown) {
  const bytes = getStatNumber(stat, 'bytesSent') ?? getStatNumber(stat, 'bytesReceived');
  const timestamp = getStatNumber(stat, 'timestamp');
  if (bytes === undefined || timestamp === undefined) return 'n/a';

  const previous = screenShareStatsSamples.get(key);
  screenShareStatsSamples.set(key, { bytes, timestamp });
  if (!previous || timestamp <= previous.timestamp) return 'warming';

  const bits = (bytes - previous.bytes) * 8;
  const seconds = (timestamp - previous.timestamp) / 1000;
  if (seconds <= 0) return 'n/a';
  return `${(bits / seconds / 1_000_000).toFixed(2)} Mbps`;
}

async function collectScreenShareStatsRow(
  key: string,
  direction: 'outbound' | 'inbound',
  participant: string,
  track: { mediaStreamTrack?: MediaStreamTrack; getRTCStatsReport?: () => Promise<RTCStatsReport | undefined> },
): Promise<ScreenShareStatsRow | null> {
  const stats = await track.getRTCStatsReport?.();
  if (!stats) return null;

  const rtp = findVideoRtpStat(stats, direction);
  if (!rtp) return null;

  const framesPerSecond = getStatNumber(rtp, 'framesPerSecond') ?? getStatNumber(rtp, 'framesPerSecondSent');
  const frameWidth = getStatNumber(rtp, 'frameWidth');
  const frameHeight = getStatNumber(rtp, 'frameHeight');

  return {
    direction,
    participant,
    requested: formatScreenShareTarget(),
    captured: formatTrackSettings(track.mediaStreamTrack),
    bitrate: calculateBitrateMbps(key, stats, rtp),
    fps: framesPerSecond !== undefined ? Math.round(framesPerSecond) : 'n/a',
    frames: frameWidth && frameHeight ? `${frameWidth}x${frameHeight}` : 'n/a',
    transport: describeTransport(stats),
  };
}

async function logScreenShareDiagnostics(room: Room) {
  const rows: ScreenShareStatsRow[] = [];
  const localPublication = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
  const localTrack = localPublication?.track as {
    mediaStreamTrack?: MediaStreamTrack;
    getRTCStatsReport?: () => Promise<RTCStatsReport | undefined>;
  } | undefined;

  if (localTrack?.getRTCStatsReport) {
    const row = await collectScreenShareStatsRow('local-screen-share', 'outbound', 'you', localTrack);
    if (row) rows.push(row);
  }

  for (const participant of room.remoteParticipants.values()) {
    const publication = participant.getTrackPublication(Track.Source.ScreenShare);
    const remoteTrack = publication?.track as {
      mediaStreamTrack?: MediaStreamTrack;
      getRTCStatsReport?: () => Promise<RTCStatsReport | undefined>;
    } | undefined;
    if (!remoteTrack?.getRTCStatsReport) continue;

    const row = await collectScreenShareStatsRow(
      `remote-screen-share:${participant.identity}`,
      'inbound',
      participant.name || participant.identity,
      remoteTrack,
    );
    if (row) rows.push(row);
  }

  if (rows.length > 0) {
    console.table(rows);
  }
}

function startScreenShareDiagnostics(room: Room) {
  if (!import.meta.env.DEV && localStorage.getItem('erozion_livekit_diagnostics') !== '1') {
    return;
  }
  if (screenShareDiagnosticsTimer) clearInterval(screenShareDiagnosticsTimer);
  screenShareStatsSamples.clear();
  void logScreenShareDiagnostics(room).catch((error) => {
    console.warn('[LiveKit screenshare diagnostics] failed:', error);
  });
  screenShareDiagnosticsTimer = setInterval(() => {
    void logScreenShareDiagnostics(room).catch((error) => {
      console.warn('[LiveKit screenshare diagnostics] failed:', error);
    });
  }, 3000);
}

function stopScreenShareDiagnostics() {
  if (screenShareDiagnosticsTimer) {
    clearInterval(screenShareDiagnosticsTimer);
    screenShareDiagnosticsTimer = null;
  }
  screenShareStatsSamples.clear();
}

function hasAnyScreenShareTrack(room: Room) {
  if (room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track) {
    return true;
  }
  for (const participant of room.remoteParticipants.values()) {
    if (participant.getTrackPublication(Track.Source.ScreenShare)?.track) {
      return true;
    }
  }
  return false;
}

function stopScreenShareDiagnosticsIfIdle(room: Room) {
  setTimeout(() => {
    if (!hasAnyScreenShareTrack(room)) {
      stopScreenShareDiagnostics();
    }
  }, 0);
}

function getProfileVoiceUser(userId: string): VoiceUser {
  const auth = useAuthStore.getState();
  const friend = useFriendStore.getState().friends.find((f) => f.friend_id === userId)?.friend;
  return {
    id: userId,
    username:
      friend?.display_name ||
      friend?.username ||
      auth.profile?.display_name ||
      auth.profile?.username ||
      'Utilisateur',
    avatar_url: friend?.avatar_url || null,
    muted: false,
  };
}

function getLocalVoiceUser(muted: boolean): VoiceUser | null {
  const auth = useAuthStore.getState();
  const userId = auth.user?.id || auth.profile?.id;
  if (!userId) return null;

  return {
    id: userId,
    username:
      auth.profile?.display_name ||
      auth.profile?.username ||
      auth.user?.username ||
      'Vous',
    avatar_url: auth.profile?.avatar_url || null,
    muted,
  };
}

function mergeLocalVoiceParticipant(channelId: string, participants: VoiceUser[], state: VoiceState): VoiceUser[] {
  if (!state.isConnected || state.currentChannelId !== channelId) {
    return participants;
  }

  const localUser = getLocalVoiceUser(state.isMuted);
  if (!localUser) {
    return participants;
  }

  return [
    localUser,
    ...participants.filter((participant) => participant.id !== localUser.id),
  ];
}

function extractLocalMicStream(room: Room): MediaStream | null {
  const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
  const localTrack = pub?.track as { mediaStreamTrack?: MediaStreamTrack } | undefined;
  if (!localTrack?.mediaStreamTrack) return null;
  return new MediaStream([localTrack.mediaStreamTrack]);
}

function startChannelMeterLoop() {
  if (channelMeterTimer) clearInterval(channelMeterTimer);
  channelMeterTimer = setInterval(() => {
    if (!channelRoom) return;

    const speaking: Record<string, number> = {};
    for (const participant of channelRoom.remoteParticipants.values()) {
      speaking[participant.identity] = participant.audioLevel;
    }

    useVoiceStore.setState({ userSpeakingStates: speaking });
  }, 250);
}

function stopChannelMeterLoop() {
  if (channelMeterTimer) {
    clearInterval(channelMeterTimer);
    channelMeterTimer = null;
  }
}

function startDmMeterLoop() {
  if (dmMeterTimer) clearInterval(dmMeterTimer);
  dmMeterTimer = setInterval(() => {
    if (!dmRoom) return;

    const speaking: Record<string, number> = {};
    for (const participant of dmRoom.remoteParticipants.values()) {
      speaking[participant.identity] = participant.audioLevel;
    }

    useVoiceStore.setState({ dmCallSpeakingStates: speaking });
  }, 250);
}

function stopDmMeterLoop() {
  if (dmMeterTimer) {
    clearInterval(dmMeterTimer);
    dmMeterTimer = null;
  }
}

function refreshChannelParticipants(channelId: string) {
  if (!channelRoom) return;

  const users = Array.from(channelRoom.remoteParticipants.values()).map(participantToUser);

  useVoiceStore.setState((state) => ({
    connectedUsers: users,
    voiceChannelParticipants: {
      ...state.voiceChannelParticipants,
      [channelId]: users,
    },
  }));
}

function refreshDmParticipants() {
  if (!dmRoom) return;

  const users = Array.from(dmRoom.remoteParticipants.values()).map(participantToUser);

  useVoiceStore.setState({ dmCallUsers: users });
}

function clearIncomingState() {
  stopAndClearIncomingTimeout();
  stopRingtone();
  useVoiceStore.setState({
    isCallIncoming: false,
    incomingCallFrom: null,
    incomingCallId: null,
  });
}

export const useVoiceStore = create<VoiceState>((set, get) => {
  const cleanupChannelConnection = () => {
    if (isCleaningChannel) return;
    isCleaningChannel = true;

    const state = get();

    stopChannelMeterLoop();
    stopScreenShareDiagnostics();
    resetAudioElements(channelAudioElements);

    if (channelRoom) {
      channelRoom.disconnect(true);
      channelRoom = null;
    }

    if (state.localStream) {
      state.localStream.getTracks().forEach((track) => track.stop());
    }

    set((prev) => ({
      isConnected: false,
      isMuted: false,
      connectedUsers: [],
      currentChannelId: null,
      currentChannelName: null,
      localStream: null,
      peerConnections: {},
      remoteStreams: {},
      userSpeakingStates: {},
      isScreenSharing: false,
      channelVideoTracks: {},
      voiceChannelParticipants: prev.voiceChannelParticipants,
    }));

    isCleaningChannel = false;
  };

  const cleanupDmConnection = (notifyRemote: boolean) => {
    if (isCleaningDm) return;
    isCleaningDm = true;

    const state = get();
    const userId = useAuthStore.getState().user?.id;

    stopDialtone();
    clearIncomingState();

    if (notifyRemote && userId && dmPartnerUserId) {
      const userChannel = ensureUserChannel(userId);
      if (state.dmCallStatus === 'dialing') {
        userChannel?.push('call_cancel', { to_user_id: dmPartnerUserId, call_id: state.dmCallId });
      } else if (state.dmCallStatus === 'connected') {
        userChannel?.push('call_end', { to_user_id: dmPartnerUserId, call_id: state.dmCallId });
      }
    }

    stopDmMeterLoop();
    stopScreenShareDiagnostics();
    resetAudioElements(dmAudioElements);

    if (dmRoom) {
      dmRoom.disconnect(true);
      dmRoom = null;
    }

    if (state.localStream) {
      state.localStream.getTracks().forEach((track) => track.stop());
    }

    dmPartnerUserId = null;

    get()._stopCallTimer();

    set({
      dmCallStatus: 'idle',
      dmCallId: null,
      dmCallUsers: [],
      dmCallPeers: {},
      dmCallRemoteStreams: {},
      dmCallSpeakingStates: {},
      localStream: null,
      isMuted: false,
      isCameraOn: false,
      isScreenSharing: false,
      dmCallVideoTracks: {},
      callDuration: 0,
      isCallPanelExpanded: false,
    });

    isCleaningDm = false;
  };

  const setupVoiceRoomEvents = (room: Room, channelId: string) => {
    room.on(RoomEvent.ParticipantConnected, () => {
      playJoinOther();
      refreshChannelParticipants(channelId);
    });

    room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
      playLeave();
      removeAudioElement(channelAudioElements, participant.identity);
      set((state) => {
        const { [participant.identity]: _removed, ...rest } = state.remoteStreams;
        return { remoteStreams: rest };
      });
      refreshChannelParticipants(channelId);
    });

    room.on(
      RoomEvent.TrackSubscribed,
      (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
        requestBestScreenShareQuality(publication);
        if (track.kind === Track.Kind.Audio) {
          attachRemoteAudio(channelAudioElements, participant.identity, track);
          set((state) => ({
            remoteStreams: {
              ...state.remoteStreams,
              [participant.identity]: new MediaStream([track.mediaStreamTrack]),
            },
          }));
        } else if (track.kind === Track.Kind.Video && track.mediaStreamTrack) {
          const source = publication.source === Track.Source.ScreenShare ? 'screen' : 'camera';
          if (source === 'screen') {
            startScreenShareDiagnostics(room);
          }
          set((state) => ({
            channelVideoTracks: {
              ...state.channelVideoTracks,
              [participant.identity]: { stream: new MediaStream([track.mediaStreamTrack!]), source },
            },
          }));
        }
        refreshChannelParticipants(channelId);
      },
    );

    room.on(
      RoomEvent.TrackUnsubscribed,
      (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Audio) {
          removeAudioElement(channelAudioElements, participant.identity);
          set((state) => {
            const { [participant.identity]: _removed, ...rest } = state.remoteStreams;
            return { remoteStreams: rest };
          });
        } else if (track.kind === Track.Kind.Video) {
          set((state) => {
            const { [participant.identity]: _r, ...rest } = state.channelVideoTracks;
            return { channelVideoTracks: rest };
          });
          if (publication.source === Track.Source.ScreenShare) {
            stopScreenShareDiagnosticsIfIdle(room);
          }
        }
        refreshChannelParticipants(channelId);
      },
    );

    room.on(RoomEvent.Disconnected, () => {
      if (isCleaningChannel) return;
      if (get().currentChannelId === channelId && get().isConnected) {
        cleanupChannelConnection();
      }
    });
  };

  const setupDmRoomEvents = (room: Room) => {
    room.on(RoomEvent.ParticipantConnected, () => {
      refreshDmParticipants();
      if (get().dmCallStatus === 'dialing') {
        stopDialtone();
        set({ dmCallStatus: 'connected' });
        get()._startCallTimer();
      }
    });

    room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
      playLeave();
      removeAudioElement(dmAudioElements, participant.identity);
      set((state) => {
        const { [participant.identity]: _streamRemoved, ...restStreams } = state.dmCallRemoteStreams;
        const { [participant.identity]: _speakingRemoved, ...restSpeaking } = state.dmCallSpeakingStates;
        return {
          dmCallRemoteStreams: restStreams,
          dmCallSpeakingStates: restSpeaking,
        };
      });
      refreshDmParticipants();

      if (room.remoteParticipants.size === 0 && get().dmCallStatus !== 'idle') {
        dmPartnerUserId = null;
        cleanupDmConnection(false);
      }
    });

    room.on(
      RoomEvent.TrackSubscribed,
      (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
        requestBestScreenShareQuality(publication);
        if (track.kind === Track.Kind.Audio) {
          attachRemoteAudio(dmAudioElements, participant.identity, track);
          set((state) => ({
            dmCallRemoteStreams: {
              ...state.dmCallRemoteStreams,
              [participant.identity]: new MediaStream([track.mediaStreamTrack]),
            },
          }));
        } else if (track.kind === Track.Kind.Video && track.mediaStreamTrack) {
          const source = publication.source === Track.Source.ScreenShare ? 'screen' : 'camera';
          if (source === 'screen') {
            startScreenShareDiagnostics(room);
          }
          set((state) => ({
            dmCallVideoTracks: {
              ...state.dmCallVideoTracks,
              [participant.identity]: { stream: new MediaStream([track.mediaStreamTrack!]), source },
            },
          }));
        }
        refreshDmParticipants();
      },
    );

    room.on(
      RoomEvent.TrackUnsubscribed,
      (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Audio) {
          removeAudioElement(dmAudioElements, participant.identity);
          set((state) => {
            const { [participant.identity]: _removed, ...rest } = state.dmCallRemoteStreams;
            return { dmCallRemoteStreams: rest };
          });
        } else if (track.kind === Track.Kind.Video) {
          set((state) => {
            const { [participant.identity]: _r, ...rest } = state.dmCallVideoTracks;
            return { dmCallVideoTracks: rest };
          });
          if (publication.source === Track.Source.ScreenShare) {
            stopScreenShareDiagnosticsIfIdle(room);
          }
        }
        refreshDmParticipants();
      },
    );

    room.on(RoomEvent.Disconnected, () => {
      if (isCleaningDm) return;
      if (get().dmCallStatus !== 'idle') {
        dmPartnerUserId = null;
        cleanupDmConnection(false);
      }
    });
  };

  const connectDmRoom = async (targetUserId: string, status: DmCallStatus) => {
    const token = await getDMCallToken(targetUserId, { canPublishVideo: true, canShareScreen: true });
    const room = createLiveKitRoom();

    setupDmRoomEvents(room);
    await room.connect(token.livekitUrl, token.token);
    await room.localParticipant.setMicrophoneEnabled(true, MIC_CONSTRAINTS);

    dmRoom = room;
    dmPartnerUserId = targetUserId;
    startDmMeterLoop();

    const localStream = extractLocalMicStream(room);
    const currentUsers = Array.from(room.remoteParticipants.values()).map(participantToUser);
    const displayUsers = currentUsers.length > 0 ? currentUsers : [getProfileVoiceUser(targetUserId)];

    set({
      localStream,
      dmCallStatus: status,
      dmCallId: token.roomName,
      dmCallUsers: displayUsers,
      dmCallPeers: {},
      dmCallRemoteStreams: {},
      dmCallSpeakingStates: {},
      isMuted: false,
      callDuration: 0,
    });

    if (status === 'connected') {
      get()._startCallTimer();
    }

    return token.roomName;
  };

  return {
    // Voice channel state
    isConnected: false,
    isMuted: false,
    connectedUsers: [],
    currentChannelId: null,
    currentChannelName: null,
    localStream: null,
    peerConnections: {},
    remoteStreams: {},
    userSpeakingStates: {},
    voiceChannelParticipants: {},

    // DM call state
    dmCallStatus: 'idle',
    dmCallId: null,
    dmCallUsers: [],
    dmCallPeers: {},
    dmCallRemoteStreams: {},
    dmCallSpeakingStates: {},
    isCameraOn: false,
    isScreenSharing: false,
    channelVideoTracks: {},
    dmCallVideoTracks: {},
    callDuration: 0,
    callTimerId: null,

    // Incoming call
    isCallIncoming: false,
    incomingCallFrom: null,
    incomingCallId: null,

    // Expanded panel
    isCallPanelExpanded: false,
    setCallPanelExpanded: (v) => set({ isCallPanelExpanded: v }),

    joinVoiceChannel: async (channelId) => {
      try {
        if (get().dmCallStatus !== 'idle') {
          cleanupDmConnection(true);
        }

        if (get().isConnected) {
          cleanupChannelConnection();
        }

        const token = await getVoiceChannelToken(channelId);
        const room = createLiveKitRoom();

        setupVoiceRoomEvents(room, channelId);
        await room.connect(token.livekitUrl, token.token);
        await room.localParticipant.setMicrophoneEnabled(true, MIC_CONSTRAINTS);

        channelRoom = room;
        startChannelMeterLoop();

        const { channels } = useServerStore.getState();
        const channelName = channels.find((c) => c.id === channelId)?.name ?? 'Voice Channel';

        const localStream = extractLocalMicStream(room);

        set((state) => ({
          isConnected: true,
          isMuted: false,
          currentChannelId: channelId,
          currentChannelName: channelName,
          localStream,
          remoteStreams: {},
          connectedUsers: [],
          userSpeakingStates: {},
          voiceChannelParticipants: {
            ...state.voiceChannelParticipants,
            [channelId]: [],
          },
        }));

        refreshChannelParticipants(channelId);
        return localStream;
      } catch (error) {
        console.error('Error joining LiveKit voice channel:', error);
        cleanupChannelConnection();
        return null;
      }
    },

    leaveVoiceChannel: () => {
      cleanupChannelConnection();
    },

    fetchVoiceChannelParticipants: async (channelId) => {
      try {
        const participants = await getVoiceChannelParticipants(channelId);
        set((state) => ({
          voiceChannelParticipants: {
            ...state.voiceChannelParticipants,
            [channelId]: mergeLocalVoiceParticipant(channelId, participants, state),
          },
        }));
      } catch (error) {
        console.warn('Failed to fetch voice participants:', channelId, error);
      }
    },

    fetchVisibleVoiceChannelParticipants: async (channelIds) => {
      const uniqueIds = Array.from(new Set(channelIds.filter(Boolean)));
      if (uniqueIds.length === 0) return;

      const results = await Promise.all(
        uniqueIds.map(async (channelId) => {
          try {
            const participants = await getVoiceChannelParticipants(channelId);
            return [channelId, participants] as const;
          } catch (error) {
            console.warn('Failed to fetch voice participants:', channelId, error);
            return null;
          }
        }),
      );

      set((state) => {
        const next = { ...state.voiceChannelParticipants };
        for (const result of results) {
          if (!result) continue;
          next[result[0]] = mergeLocalVoiceParticipant(result[0], result[1], state);
        }
        return { voiceChannelParticipants: next };
      });
    },

    toggleMute: () => {
      const nextMuted = !get().isMuted;

      if (channelRoom) {
        void channelRoom.localParticipant.setMicrophoneEnabled(!nextMuted, MIC_CONSTRAINTS);
      }
      if (dmRoom) {
        void dmRoom.localParticipant.setMicrophoneEnabled(!nextMuted, MIC_CONSTRAINTS);
      }

      const localStream = get().localStream;
      if (localStream) {
        localStream.getAudioTracks().forEach((track) => {
          track.enabled = !nextMuted;
        });
      }

      set((state) => {
        const nextParticipants = { ...state.voiceChannelParticipants };
        if (state.currentChannelId) {
          nextParticipants[state.currentChannelId] = mergeLocalVoiceParticipant(
            state.currentChannelId,
            nextParticipants[state.currentChannelId] || [],
            { ...state, isMuted: nextMuted },
          );
        }
        return { isMuted: nextMuted, voiceChannelParticipants: nextParticipants };
      });
    },

    startDmCall: async (recipientId) => {
      try {
        if (get().isConnected) {
          cleanupChannelConnection();
        }
        if (get().dmCallStatus !== 'idle') {
          cleanupDmConnection(true);
        }

        const userId = useAuthStore.getState().user?.id;
        if (!userId) return;

        const roomName = await connectDmRoom(recipientId, 'dialing');
        if (!roomName) return;

        playDialtone();

        const auth = useAuthStore.getState();
        const displayName =
          auth.profile?.display_name ||
          auth.profile?.username ||
          auth.user?.username ||
          'Utilisateur';
        const avatar = auth.profile?.avatar_url || '';

        const userChannel = ensureUserChannel(userId);
        userChannel?.push('call_invite', {
          to_user_id: recipientId,
          display_name: displayName,
          avatar_url: avatar,
          call_id: roomName,
        });
      } catch (error) {
        console.error('Error starting DM call with LiveKit:', error);
        cleanupDmConnection(false);
      }
    },

    joinDmCall: async (callId) => {
      const state = get();
      if (state.isCallIncoming && state.incomingCallId === callId) {
        await state.answerDmCall();
      }
    },

    answerDmCall: async () => {
      const state = get();
      const caller = state.incomingCallFrom;
      if (!caller) return;

      try {
        if (state.isConnected) {
          cleanupChannelConnection();
        }
        if (state.dmCallStatus !== 'idle') {
          cleanupDmConnection(true);
        }

        const userId = useAuthStore.getState().user?.id;
        if (userId && caller.id) {
          const userChannel = ensureUserChannel(userId);
          userChannel?.push('call_answer', {
            to_user_id: caller.id,
            call_id: state.incomingCallId,
          });
        }

        clearIncomingState();
        await connectDmRoom(caller.id, 'connected');
      } catch (error) {
        console.error('Error answering DM call with LiveKit:', error);
        cleanupDmConnection(false);
      }
    },

    leaveDmCall: () => {
      cleanupDmConnection(true);
    },

    rejectIncomingCall: () => {
      const userId = useAuthStore.getState().user?.id;
      const caller = get().incomingCallFrom;
      const callId = get().incomingCallId;
      if (userId && caller?.id) {
        const userChannel = ensureUserChannel(userId);
        userChannel?.push('call_reject', { to_user_id: caller.id, call_id: callId });
      }
      clearIncomingState();
    },

    toggleCamera: async () => {
      if (!dmRoom) return;
      const nextOn = !get().isCameraOn;
      try {
        await dmRoom.localParticipant.setCameraEnabled(nextOn);
        set({ isCameraOn: nextOn });
      } catch (e) {
        console.error('Camera toggle error:', e);
      }
    },

    toggleScreenShare: async () => {
      const room = channelRoom ?? dmRoom;
      if (!room) return;
      const nextOn = !get().isScreenSharing;
      try {
        const captureOptions = buildScreenShareCaptureOptions();
        const publishOptions = buildScreenSharePublishOptions();
        await room.localParticipant.setScreenShareEnabled(
          nextOn,
          captureOptions,
          publishOptions,
        );
        set({ isScreenSharing: nextOn });
        if (nextOn) {
          startScreenShareDiagnostics(room);
        } else {
          stopScreenShareDiagnosticsIfIdle(room);
        }
      } catch (e: unknown) {
        const isDomError =
          e instanceof DOMException ||
          (typeof e === 'object' && e !== null && 'name' in e);

        if (nextOn && isDomError) {
          // Electron / restricted context: fall back to getDisplayMedia + publish manually
          try {
            const settings = getScreenShareSettings();
            const resolution = getScreenShareResolution(settings);
            const stream = await navigator.mediaDevices.getDisplayMedia({
              video: {
                width: { ideal: resolution.width },
                height: { ideal: resolution.height },
                frameRate: { ideal: settings.fps, max: settings.fps },
              },
              audio: false,
            });
            const videoTrack = stream.getVideoTracks()[0];
            if (videoTrack) {
              videoTrack.contentHint = 'detail';
              await room.localParticipant.publishTrack(videoTrack, buildScreenSharePublishOptions());
              videoTrack.onended = () => {
                void room.localParticipant.setScreenShareEnabled(false).catch(() => {});
                stopScreenShareDiagnosticsIfIdle(room);
                set({ isScreenSharing: false });
              };
              set({ isScreenSharing: true });
              startScreenShareDiagnostics(room);
            }
          } catch (fallbackErr) {
            console.error('Screen share fallback error:', fallbackErr);
          }
        } else {
          if (get().isScreenSharing) set({ isScreenSharing: false });
          console.error('Screen share error:', e);
        }
      }
    },

    _handleUserJoined: (user) => {
      set((state) => {
        if (state.connectedUsers.some((u) => u.id === user.id)) return state;
        return { connectedUsers: [...state.connectedUsers, user] };
      });
    },

    _handleUserLeft: (userId) => {
      set((state) => ({
        connectedUsers: state.connectedUsers.filter((u) => u.id !== userId),
      }));
    },

    _handleUserMuteChanged: (userId, muted) => {
      set((state) => ({
        connectedUsers: state.connectedUsers.map((u) =>
          u.id === userId ? { ...u, muted } : u,
        ),
      }));
    },

    _cleanupPeerConnections: () => {
      set({ peerConnections: {}, dmCallPeers: {} });
    },

    _createChannelPeer: () => null,

    _createDmPeer: () => null,

    _startCallTimer: () => {
      const existing = get().callTimerId;
      if (existing) clearInterval(existing);

      const timerId = setInterval(() => {
        set((state) => ({ callDuration: state.callDuration + 1 }));
      }, 1000);

      set({ callTimerId: timerId });
    },

    _stopCallTimer: () => {
      const timerId = get().callTimerId;
      if (!timerId) return;
      clearInterval(timerId);
      set({ callTimerId: null });
    },
  };
});

function attachGlobalSocketListeners() {
  const userId = useAuthStore.getState().user?.id || localStorage.getItem('user_id');
  if (!userId) return false;

  const userChannel = ensureUserChannel(userId);
  if (!userChannel) return false;

  if (listenersAttached && attachedUserId === userId) {
    return true;
  }

  listenersAttached = true;
  attachedUserId = userId;

  userChannel.on(
    'call_incoming',
    (payload: {
      from_user_id?: string;
      display_name?: string;
      avatar_url?: string;
      call_id?: string;
    }) => {
      const state = useVoiceStore.getState();
      const fromUserId = payload.from_user_id || '';
      if (!fromUserId) return;

      if (state.isConnected || state.dmCallStatus !== 'idle' || state.isCallIncoming) {
        const currentUserId = useAuthStore.getState().user?.id;
        if (currentUserId) {
          const ch = ensureUserChannel(currentUserId);
          ch?.push('call_reject', { to_user_id: fromUserId, call_id: payload.call_id || '' });
        }
        return;
      }

      playRingtone();

      useVoiceStore.setState({
        isCallIncoming: true,
        incomingCallFrom: {
          id: fromUserId,
          username: payload.display_name || 'Utilisateur',
          avatar_url: payload.avatar_url || null,
          muted: false,
        },
        incomingCallId: payload.call_id || fromUserId,
      });

      stopAndClearIncomingTimeout();
      incomingCallTimeout = setTimeout(() => {
        const s = useVoiceStore.getState();
        if (s.isCallIncoming && s.incomingCallFrom?.id === fromUserId) {
          const currentUserId = useAuthStore.getState().user?.id;
          if (currentUserId && s.incomingCallId) {
            const ch = ensureUserChannel(currentUserId);
            ch?.push('call_missed', {
              to_user_id: fromUserId,
              call_id: s.incomingCallId,
            });
          }
          clearIncomingState();
        }
      }, 30_000);
    },
  );

  userChannel.on('call_cancelled', (payload: { from_user_id?: string; call_id?: string }) => {
    const fromUserId = payload.from_user_id || '';
    const callId = payload.call_id || '';
    if (!fromUserId || !callId) return;

    const state = useVoiceStore.getState();
    if (state.isCallIncoming && state.incomingCallFrom?.id === fromUserId && state.incomingCallId === callId) {
      clearIncomingState();
      return;
    }

    if (state.dmCallStatus === 'dialing' && dmPartnerUserId === fromUserId && state.dmCallId === callId) {
      dmPartnerUserId = null;
      state.leaveDmCall();
    }
  });

  userChannel.on('call_rejected', (payload: { from_user_id?: string; call_id?: string }) => {
    const fromUserId = payload.from_user_id || '';
    const callId = payload.call_id || '';
    if (!fromUserId || !callId) return;

    const state = useVoiceStore.getState();
    if (state.dmCallStatus === 'dialing' && dmPartnerUserId === fromUserId && state.dmCallId === callId) {
      dmPartnerUserId = null;
      state.leaveDmCall();
    }
  });

  userChannel.on('call_answered', (payload: { from_user_id?: string; to_user_id?: string; call_id?: string }) => {
    const fromUserId = payload.from_user_id || '';
    const toUserId = payload.to_user_id || '';
    const callId = payload.call_id || '';
    if (!callId) return;

    const state = useVoiceStore.getState();
    if (state.isCallIncoming) {
      const incomingFromId = state.incomingCallFrom?.id || '';
      const samePeer = (toUserId && incomingFromId === toUserId) || (fromUserId && incomingFromId === fromUserId);
      if (samePeer && state.incomingCallId === callId) {
        clearIncomingState();
      }
    }
  });

  userChannel.on('call_ended', (payload: { from_user_id?: string; call_id?: string }) => {
    const fromUserId = payload.from_user_id || '';
    const callId = payload.call_id || '';
    if (!fromUserId || !callId) return;

    const state = useVoiceStore.getState();
    if (
      (state.dmCallStatus === 'connected' || state.dmCallStatus === 'dialing') &&
      dmPartnerUserId === fromUserId &&
      state.dmCallId === callId
    ) {
      dmPartnerUserId = null;
      state.leaveDmCall();
    }
  });

  userChannel.on('call_missed', (payload: { from_user_id?: string; call_id?: string }) => {
    const fromUserId = payload.from_user_id || '';
    const callId = payload.call_id || '';
    if (!fromUserId || !callId) return;

    const state = useVoiceStore.getState();
    if (state.isCallIncoming && state.incomingCallFrom?.id === fromUserId && state.incomingCallId === callId) {
      clearIncomingState();
      return;
    }

    if (state.dmCallStatus === 'dialing' && dmPartnerUserId === fromUserId && state.dmCallId === callId) {
      dmPartnerUserId = null;
      state.leaveDmCall();
    }
  });

  return true;
}

function tryAttachListeners() {
  return attachGlobalSocketListeners();
}

if (!tryAttachListeners()) {
  let attempts = 0;
  const interval = setInterval(() => {
    attempts += 1;
    if (tryAttachListeners() || attempts > 20) {
      clearInterval(interval);
    }
  }, 500);
}
